/**
 * Shared types for LIMS API (server and client)
 *
 * These types are used by both the API server and the API client to ensure
 * type consistency across the API boundary.
 *
 * The *_ATTRIBUTE_DEFS arrays are the SINGLE source of truth for which
 * attributes exist per entity type. They serve two purposes:
 *   1. Runtime: the server registers them in the DB at startup.
 *   2. Compile-time: TypeScript attribute interfaces are DERIVED from them
 *      via mapped types — no manual duplication needed.
 */
import { validateAssayAnalytes } from './assay-analytes.js';
import { validateAssayPanel } from './assay-panel.js';
import { ASSAY_CAPTURES, ASSAY_STATES, validateSampleTypes, validateUploadMapping } from './assay-metadata.js';
/**
 * Creates a validator that normalizes text values to a canonical form.
 * Matching is case-insensitive. Unrecognised values are rejected.
 */
export function textEnum(canonicalValues, aliases) {
    const lowerMap = new Map();
    for (const v of canonicalValues)
        lowerMap.set(v.toLowerCase(), v);
    if (aliases) {
        for (const [alias, canonical] of Object.entries(aliases))
            lowerMap.set(alias.toLowerCase(), canonical);
    }
    return (value) => {
        const lower = String(value).toLowerCase();
        const canonical = lowerMap.get(lower);
        if (canonical !== undefined)
            return { value: canonical };
        return { error: `Invalid value '${value}'. Allowed: ${canonicalValues.join(', ')}` };
    };
}
// ============================================================================
// Attribute definitions (runtime + compile-time source of truth)
//
// To add a new attribute: add one entry here. The TypeScript type updates
// automatically — no separate interface to maintain.
// ============================================================================
/** Where a sample was last scanned, in the order it moves through the Copper One sample prep room. Set by the Sample Prep page. */
export const SAMPLE_LOCATIONS = ['WAITING_FOR_DRYER', 'IN_DRYER', 'SAMPLE_PREP'];
export const SAMPLE_ATTRIBUTE_DEFS = [
    { name: 'attachment_count', data_type: 'number' },
    { name: 'batch_number', data_type: 'text' },
    { name: 'collection_site', data_type: 'text' },
    { name: 'collection_timestamp', data_type: 'date' },
    { name: 'complete', data_type: 'boolean' },
    { name: 'date_completed', data_type: 'date' },
    { name: 'date_created', data_type: 'date' },
    { name: 'date_received', data_type: 'date' },
    { name: 'has_report', data_type: 'boolean' },
    { name: 'id_hash', data_type: 'text' },
    { name: 'last_updated', data_type: 'date' },
    { name: 'linked', data_type: 'boolean' },
    { name: 'lot_number', data_type: 'text' },
    {
        name: 'mm_bag_id',
        data_type: 'text',
        description: 'Barcode of the bag the sample arrives in, one sample per bag; scanned at receiving to find the sample',
        orderable: { unique: true },
    },
    { name: 'mm_is_top_priority', data_type: 'boolean' },
    {
        name: 'mm_location',
        data_type: 'text',
        description: 'Where the sample was last scanned, one of SAMPLE_LOCATIONS',
        validate: textEnum(SAMPLE_LOCATIONS),
    },
    { name: 'mm_location_at', data_type: 'date', description: 'When mm_location was scanned' },
    { name: 'mm_location_by', data_type: 'text', description: 'Who scanned mm_location' },
    { name: 'mm_los_experiment_campaign_description', data_type: 'text' },
    { name: 'mm_los_experiment_campaign_id', data_type: 'text' },
    { name: 'mm_los_experiment_campaign_name', data_type: 'text' },
    { name: 'mm_los_experiment_description', data_type: 'text' },
    { name: 'mm_los_experiment_id', data_type: 'text' },
    { name: 'mm_los_experiment_name', data_type: 'text' },
    { name: 'mm_los_experiment_unit_operation_id', data_type: 'text' },
    { name: 'mm_los_experiment_unit_operation_name', data_type: 'text' },
    { name: 'mm_needed_by_hours', data_type: 'text' },
    { name: 'mm_priority_reason', data_type: 'text' },
    { name: 'mm_program_id', data_type: 'text' },
    { name: 'mm_sample_lab_process_type', data_type: 'text' },
    { name: 'mm_sample_type', data_type: 'text' },
    { name: 'mm_sample_mass_g', data_type: 'number', description: 'Mass of sample added at intake, in grams' },
    { name: 'order_id', data_type: 'number', referenced_entity_type: 'order' },
    { name: 'order_request', data_type: 'boolean' },
    { name: 'parent_batch_number', data_type: 'text' },
    { name: 'partially_complete', data_type: 'boolean' },
    { name: 'received', data_type: 'boolean' },
    { name: 'report_emailed', data_type: 'boolean' },
    { name: 'notification_emails', data_type: 'json', description: 'Email addresses notified when this sample completes' },
    { name: 'retain_period_weeks', data_type: 'text' },
    { name: 'sample_description', data_type: 'text' },
    { name: 'sample_matrix', data_type: 'text' },
    { name: 'sample_name', data_type: 'text' },
    { name: 'sample_point', data_type: 'text' },
    { name: 'sample_received', data_type: 'boolean' },
    { name: 'sample_type', data_type: 'text' },
    { name: 'stream_type', data_type: 'text' },
    { name: 'test_count', data_type: 'number' },
    { name: 'unit_number', data_type: 'text' },
    { name: 'collection_datestamp', data_type: 'text' },
    { name: 'lab_id', data_type: 'number', referenced_entity_type: 'lab' },
    { name: 'parsed_from', data_type: 'text' },
    { name: 'pilot_data', data_type: 'json' },
    { name: 'etx_data', data_type: 'json' },
];
export const LAB_ORDER_SAMPLE_ATTRIBUTE_DEFS = SAMPLE_ATTRIBUTE_DEFS.filter((def) => def.orderable);
/**
 * Suspect-flag lifecycle (POS-1814). A result is suspect
 * while `suspect_status IS NOT NULL AND suspect_status != 'RESOLVED'` —
 * downstream consumers should apply that rule rather than enumerate values, so
 * new statuses default to "still suspect". CANNOT RERUN, WONT RERUN and
 * INVALIDATED are terminal but not resolved; only RESOLVED (a re-run overwrote
 * the result) clears the flag. Must match CapitalOS
 * client/web-app/src/services/lims/types.ts and be released BEFORE CapitalOS
 * writes a new value (the server rejects undeclared enum values with a 400).
 */
export const LIMS_SUSPECT_STATUSES = [
    'SUSPECT',
    'RERUN REQUESTED',
    'IN PROGRESS',
    'RESOLVED',
    'CANNOT RERUN',
    'WONT RERUN',
    'INVALIDATED',
];
export const TEST_ATTRIBUTE_DEFS = [
    { name: 'assay_id', data_type: 'number', referenced_entity_type: 'assay' },
    { name: 'lab_id', data_type: 'number', referenced_entity_type: 'lab' },
    { name: 'comments', data_type: 'text' },
    { name: 'complete_date', data_type: 'date' },
    { name: 'date_created', data_type: 'date' },
    { name: 'emailed', data_type: 'boolean' },
    { name: 'has_report', data_type: 'boolean' },
    { name: 'icp_measurement_date', data_type: 'date' },
    { name: 'icp_dmc_correction_factor', data_type: 'number' },
    { name: 'icp_xml_data', data_type: 'json' },
    { name: 'last_updated', data_type: 'text' },
    { name: 'last_updated_unix_timestamp', data_type: 'number' },
    { name: 'panel_reflex', data_type: 'json',
        description: 'On a panel test: what the reflex last decided (each rule\'s measured value, threshold and outcome, the tests it added, flags)' },
    { name: 'parent_status', data_type: 'text' },
    { name: 'publish_worksheet_to_portal', data_type: 'boolean' },
    { name: 'raw_worksheet_data', data_type: 'json' },
    { name: 'release_report', data_type: 'boolean' },
    { name: 'report_emailed', data_type: 'boolean' },
    { name: 'results', data_type: 'text' },
    { name: 'sample_id', data_type: 'number', referenced_entity_type: 'sample' },
    { name: 'specification_overall_status', data_type: 'text' },
    { name: 'start_date', data_type: 'date' },
    { name: 'state', data_type: 'text', validate: textEnum(['NOT STARTED', 'IN PROGRESS', 'COMPLETED', 'CANCELLED'], { pending: 'NOT STARTED', started: 'IN PROGRESS', in_progress: 'IN PROGRESS', complete: 'COMPLETED', canceled: 'CANCELLED' }) },
    { name: 'suspect_flagged_at', data_type: 'date',
        description: 'When this result was flagged as suspect' },
    { name: 'suspect_flagged_by', data_type: 'text',
        description: 'Display name of the user who flagged this result as suspect' },
    { name: 'suspect_flagged_check', data_type: 'text',
        description: 'Automated check that recorded the flagged value, e.g. cu_sigma' },
    { name: 'suspect_flagged_standard_deviation', data_type: 'number',
        description: 'Standard deviation the automated check judged the flagged value with' },
    { name: 'suspect_flagged_value', data_type: 'number',
        description: 'Result value when the automated check flagged it; a re-run is judged against it' },
    { name: 'suspect_reason', data_type: 'text',
        description: 'Why this result was flagged as suspect' },
    { name: 'suspect_resolution', data_type: 'text',
        description: 'How a suspect result was resolved, written when the flag closes' },
    { name: 'suspect_sigma', data_type: 'number',
        description: 'How many (floored/capped) standard deviations the flagged value sat from its history mean' },
    { name: 'suspect_status', data_type: 'text',
        description: 'Set when a result is flagged as suspect / needing re-review',
        validate: textEnum(LIMS_SUSPECT_STATUSES) },
    { name: 'tech_id', data_type: 'number' },
    { name: 'worksheet_data', data_type: 'json' },
    { name: 'worksheet_processed', data_type: 'boolean' },
    { name: 'worksheet_processed_error', data_type: 'boolean' },
    { name: 'parsed_from', data_type: 'text' },
];
export const ASSAY_ATTRIBUTE_DEFS = [
    /** Capital OS filters on these. */
    { name: 'active', data_type: 'boolean' },
    { name: 'visible', data_type: 'boolean' },
    { name: 'analytes', data_type: 'json',
        description: 'What this assay produces: one entry per worksheet_data key (analyte name, kind result|input|derived|qc|meta, unit, headline rule, inputs of a derived value). See docs/assay-analytes-plan.md',
        validate: validateAssayAnalytes },
    { name: 'capture', data_type: 'text',
        description: 'How results arrive: manual (typed into Routine Sample Management), upload (a file through the Upload results page), instrument (a sync job)',
        validate: textEnum(ASSAY_CAPTURES) },
    { name: 'description', data_type: 'text', description: 'What the assay measures and how results are derived, in the lab\'s words (from the request form)' },
    { name: 'last_updated', data_type: 'date' },
    { name: 'method', data_type: 'text', description: 'Instrument or technique, e.g. "ARCOS ICP", "Omni", "Shimadzu TOC-L", "Titration"' },
    { name: 'panel', data_type: 'json',
        description: 'Makes this a panel assay: the assays its test starts on the sample and the reflex rules that add more (when <assay> <key> <op> <constant>, add [...]). A panel has no results of its own',
        validate: validateAssayPanel },
    { name: 'mm_run_time_min', data_type: 'number', description: 'Nominal run time per sample, minutes (Capital OS uptime estimates)' },
    { name: 'sample_types', data_type: 'json', description: 'Sample types the assay runs on',
        validate: validateSampleTypes },
    { name: 'sop_url', data_type: 'text', description: 'Link to the SOP or method document' },
    { name: 'state', data_type: 'text', description: 'draft (being set up), active, retired',
        validate: textEnum(ASSAY_STATES) },
    { name: 'title', data_type: 'text' },
    { name: 'upload_mapping', data_type: 'json', description: 'For capture=upload: how a CSV/XLSX column maps onto each analyte key (header row, sample id column, one entry per column)',
        validate: validateUploadMapping },
];
export const ORDER_ATTRIBUTE_DEFS = [
    { name: 'customer_account_id', data_type: 'number' },
    { name: 'date_approved', data_type: 'date' },
    { name: 'date_completed', data_type: 'date' },
    { name: 'date_created', data_type: 'date' },
    { name: 'date_received', data_type: 'date' },
    { name: 'date_requested', data_type: 'date' },
    { name: 'division_id', data_type: 'number' },
    { name: 'entered_by_api_client_id', data_type: 'text' },
    { name: 'entered_by_id', data_type: 'number' },
    { name: 'last_updated', data_type: 'date' },
    { name: 'mm_consumer', data_type: 'text', description: 'Lab consumer key that submitted the order through POST /lab-orders' },
    { name: 'mm_idempotency_key', data_type: 'text', description: 'Idempotency-Key of the POST /lab-orders request that created the order, unique per mm_consumer' },
    { name: 'mm_idempotency_hash', data_type: 'text', description: 'SHA-256 of the normalised POST /lab-orders request that created the order; a replay of its Idempotency-Key resumes only when it matches' },
    { name: 'mm_stream', data_type: 'text', description: 'CapitalOS lab-order stream' },
    { name: 'order_request', data_type: 'boolean' },
    { name: 'order_request_status', data_type: 'text', validate: textEnum(['DRAFT', 'SUBMITTED', 'RECEIVED', 'APPROVED']) },
    { name: 'portal_read', data_type: 'boolean' },
    { name: 'published_to_portal', data_type: 'boolean' },
    { name: 'received_by_id', data_type: 'number' },
    { name: 'requested_by_id', data_type: 'number' },
    { name: 'state', data_type: 'text', validate: textEnum(['CREATED', 'RECEIVED', 'IN PROGRESS', 'COMPLETED', 'CANCELLED'], { in_progress: 'IN PROGRESS', complete: 'COMPLETED', canceled: 'CANCELLED' }) },
    { name: 'submitted_by', data_type: 'text' },
    { name: 'total_samples_created', data_type: 'number' },
    { name: 'lab_id', data_type: 'number', referenced_entity_type: 'lab' },
];
export const ATTACHMENT_ATTRIBUTE_DEFS = [
    { name: 'parent_entity_id', data_type: 'number' },
    { name: 'parent_entity_type', data_type: 'text' },
    { name: 'filename', data_type: 'text' },
    { name: 'content_type', data_type: 'text' },
    { name: 'content_hash', data_type: 'text' },
    { name: 'file_size', data_type: 'number' },
    { name: 'data', data_type: 'json' },
    { name: 'uploaded_by', data_type: 'text' },
];
export const LAB_ATTRIBUTE_DEFS = [
    { name: 'name', data_type: 'text' },
    { name: 'description', data_type: 'text' },
    { name: 'timezone', data_type: 'text' },
];
/**
 * All entity attribute definitions keyed by entity type.
 * Used by the server to register attributes at startup.
 */
export const ENTITY_ATTRIBUTE_DEFS = {
    sample: SAMPLE_ATTRIBUTE_DEFS,
    test: TEST_ATTRIBUTE_DEFS,
    assay: ASSAY_ATTRIBUTE_DEFS,
    order: ORDER_ATTRIBUTE_DEFS,
    attachment: ATTACHMENT_ATTRIBUTE_DEFS,
    lab: LAB_ATTRIBUTE_DEFS,
};
